"use client";

import React from "react";
import { Fingerprint, DraftingCompass, Cpu, Activity, Crosshair } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
const focusAreas = [{
  id: "CHR-01",
  title: "Character",
  description: "Integrity, leadership, and coachability on and off the ice. The foundation of a reliable teammate and future prospect.",
  icon: Fingerprint
}, {
  id: "SKL-02",
  title: "Skill",
  description: "Technical proficiency, elite skating mechanics, and precision puck handling under high-pressure game situations.",
  icon: DraftingCompass
}, {
  id: "HIQ-03",
  title: "Hockey IQ",
  description: "Spatial awareness, rapid decision making, and the cognitive ability to anticipate plays before they fully develop.",
  icon: Cpu
}, {
  id: "CMP-04",
  title: "Compete Level",
  description: "Relentless work ethic, battle-winning mentality, and unwavering resilience in high-stakes, physical environments.",
  icon: Activity
}];
export default function OurScoutingFocus() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-scouting-focus" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:32px_32px]" />
      
      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div ref={headerRef} className={cn("max-w-2xl mb-16 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="flex items-center gap-2 mb-4">
            <Crosshair className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <span className="text-primary font-mono text-sm tracking-wider uppercase">Evaluation Matrix</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 uppercase">
            Our Scouting <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/50">Focus</span>
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            We analyze players through a comprehensive, data-driven lens. Our evaluation process isolates four critical pillars of elite performance to identify true advancement potential.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 pb-12 lg:pb-24">
          {focusAreas.map((area, i) => {
          const {
            ref: cardRef,
            isVisible: isCardVisible
          } = useRevealOnScroll({
            threshold: 0.1,
            once: true
          });
          const isOffset = i === 0 || i === 2;
          return <div key={area.id} data-index={i} ref={cardRef} className={cn("transition-all duration-700 ease-out h-full", isCardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", isOffset ? "lg:translate-y-12" : "lg:translate-y-0", i === 0 ? "delay-100" : i === 1 ? "delay-200" : i === 2 ? "delay-300" : "delay-400")}>
                <Card className="h-full bg-card/40 backdrop-blur-md border-border/50 rounded-sm relative overflow-hidden group hover:border-primary/50 transition-colors duration-300">
                  {}
                  <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/40 m-2" />
                  <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-primary/40 m-2" />
                  <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-primary/40 m-2" />
                  <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-primary/40 m-2" />
                  
                  {}
                  <div className="absolute -right-16 -top-16 w-32 h-32 bg-primary/5 rotate-45 group-hover:bg-primary/10 transition-colors duration-500 pointer-events-none" />

                  <CardHeader className="pb-4 relative z-10">
                    <div className="flex justify-between items-start mb-4">
                      <div className="p-3 bg-background/80 border border-border/50 rounded-sm">
                        <area.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                      </div>
                      <span className="font-mono text-xs text-muted-foreground/60 tracking-widest">
                        {area.id}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-bold tracking-wide uppercase">
                      {area.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="relative z-10">
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {area.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-12 flex justify-center lg:justify-start">
          <Button asChild size="lg" className="rounded-sm font-mono uppercase tracking-wider">
            <Link to="/PlayerInquiryPage">
              Submit Player Profile
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}